import { moveById, type MoveDef } from "../data/moves.ts";
import { speciesById, type SpeciesId } from "../data/species.ts";
import type { TypeId } from "../data/types.ts";
import { uidFromRng, type Rng } from "../engine/rng.ts";

export const LEVEL_CAP = 50;

export interface MoveSlot {
  id: string;
  pp: number;
}

export interface Mon {
  uid: string;
  speciesId: SpeciesId;
  nick: string;
  level: number;
  exp: number;
  hp: number;
  drip: { hp: number; atk: number; def: number; spd: number };
  moves: MoveSlot[];
  /** Rare alternate colouring (wild encounters only); absent rather than false. */
  shiny?: true;
}

export function statOf(m: Mon, key: "hp" | "atk" | "def" | "spd"): number {
  const sp = speciesById(m.speciesId);
  const base = sp[key];
  const d = m.drip[key];
  if (key === "hp") return Math.floor(((base * 2 + d) * m.level) / 100) + m.level + 10;
  return Math.floor(((base * 2 + d) * m.level) / 100) + 5;
}

export function maxHp(m: Mon): number {
  return statOf(m, "hp");
}

/** Rough worth of an attack for picking which slot a new move takes: expected power, STAB counted. */
function attackWorth(m: MoveDef, types: readonly TypeId[]): number {
  const hits = m.fx?.kind === "multi" ? (m.fx.min + m.fx.max) / 2 : 1;
  return m.power * hits * (m.acc / 100) * (types.includes(m.type) ? 1.25 : 1);
}

/**
 * The slot a newly learned move takes when a wild or trainer memeling mints past an offer's level.
 * Deliberately conservative, so every designed kit keeps its tactics (guard breaks, heals, setup):
 * a stronger hit replaces the weakest hit of the same type, coverage only takes the place of a
 * doubled-up type or a third attack, and support only swaps for support of the same kind.
 * -1 means the kit keeps what it has. The player's own crew sees every offer as learn/forget/skip.
 */
export function slotForNewMove(kit: readonly string[], id: string, types: readonly TypeId[]): number {
  const nm = moveById(id);
  const slots = kit.map((_, i) => i);
  const def = (i: number) => moveById(kit[i]!);
  const attacks = slots.filter((i) => def(i).power > 0);
  const weakest = (list: number[]) => list.reduce((w, i) => (w < 0 || attackWorth(def(i), types) < attackWorth(def(w), types) ? i : w), -1);
  if (nm.power > 0) {
    if (types.includes(nm.type)) {
      const same = attacks.filter((i) => def(i).type === nm.type);
      const w = weakest(same.length ? same : attacks);
      return w >= 0 && attackWorth(def(w), types) < attackWorth(nm, types) ? w : -1;
    }
    const doubled = attacks.filter((i) => attacks.some((j) => j !== i && def(j).type === def(i).type));
    if (doubled.length) return weakest(doubled);
    return attacks.length >= 3 ? weakest(attacks) : -1;
  }
  return slots.find((i) => def(i).power === 0 && def(i).fx?.kind === nm.fx?.kind) ?? -1;
}

/**
 * The four moves a memeling of this species knows at `level`: its starting kit, updated by every
 * level-up offer it would have taken by then. Wild memelings, raid bosses and Crowns mint with this,
 * so deeper grass brings coverage and stronger moves. Trainer partners keep their designed kit.
 */
export function kitAtLevel(speciesId: SpeciesId, level: number): string[] {
  const sp = speciesById(speciesId);
  const kit = [...sp.moves];
  const offers = (sp.learnset ?? []).filter((e) => e.level > 1 && e.level <= level).sort((a, b) => a.level - b.level);
  for (const e of offers) {
    if (kit.includes(e.id)) continue;
    const slot = slotForNewMove(kit, e.id, sp.types);
    if (slot >= 0) kit[slot] = e.id;
  }
  return kit;
}

/** Below this level, wild memelings leave their heavy hitters at home. */
export const EARLY_LEVEL = 8;
/** The heaviest move a wild memeling below EARLY_LEVEL carries. */
export const EARLY_POWER = 55;

/**
 * What a wild memeling knows: its level kit, minus moves above EARLY_POWER while it is below
 * EARLY_LEVEL, so a Lv5 starter wins its first fights. A kit of only heavy attacks keeps its
 * weakest one. Caught partners remember the held-back moves at EARLY_LEVEL (see learnAtLevel).
 */
export function wildKit(speciesId: SpeciesId, level: number): string[] {
  const kit = kitAtLevel(speciesId, level);
  if (level >= EARLY_LEVEL) return kit;
  const light = kit.filter((id) => moveById(id).power <= EARLY_POWER);
  if (light.some((id) => moveById(id).power > 0)) return light;
  const weakest = kit.filter((id) => moveById(id).power > 0).sort((a, b) => moveById(a).power - moveById(b).power)[0];
  return weakest ? [...light, weakest] : kit;
}

/**
 * `kit: "designed"` mints the species' starting kit as authored (starters, trainer partners, scripted
 * scenes); "level" mints what its level has reached (raids, Crowns, encores); "wild" is the level kit
 * with the early-grass cap.
 */
export function mintMon(rng: Rng, speciesId: SpeciesId, level: number, kit: "level" | "designed" | "wild" = "level"): Mon {
  const sp = speciesById(speciesId);
  const mon: Mon = {
    uid: uidFromRng(rng),
    speciesId,
    nick: sp.name,
    level: Math.max(1, Math.min(LEVEL_CAP, Math.floor(level))),
    exp: 0,
    hp: 1,
    drip: {
      hp: rng.int(0, 15),
      atk: rng.int(0, 15),
      def: rng.int(0, 15),
      spd: rng.int(0, 15),
    },
    moves: [],
  };
  const ids = kit === "designed" ? [...sp.moves] : kit === "wild" ? wildKit(speciesId, mon.level) : kitAtLevel(speciesId, mon.level);
  mon.moves = ids.map((id) => ({ id, pp: moveById(id).pp }));
  mon.hp = maxHp(mon);
  return mon;
}

export function cloneMon(m: Mon): Mon {
  return {
    ...m,
    drip: { ...m.drip },
    moves: m.moves.map((s) => ({ ...s })),
  };
}

export function usableMoves(m: Mon): MoveSlot[] {
  return m.moves.filter((s) => s.pp > 0);
}

export function moveDef(slot: MoveSlot): MoveDef {
  return moveById(slot.id);
}

export function healMon(m: Mon, amount: number): number {
  const before = m.hp;
  m.hp = Math.min(maxHp(m), m.hp + amount);
  return m.hp - before;
}

export function restoreAll(m: Mon): void {
  m.hp = maxHp(m);
  for (const slot of m.moves) slot.pp = moveById(slot.id).pp;
}

/** From here the curve steepens, so the last levels are earned in the Sidechain rather than on the way. */
export const LATE_LEVEL = 40;

export function expToNext(level: number): number {
  const base = 12 + level * 8;
  return level < LATE_LEVEL ? base : Math.round(base * (1 + (level - LATE_LEVEL + 1) * 0.12));
}

/** Evolution never refills PP, including exhausted inherited moves in old saves. */
export function upgradeMoves(m: Mon): string[] {
  const upgrades = speciesById(m.speciesId).moveUpgrades;
  const learned: string[] = [];
  if (!upgrades) return learned;
  m.moves = m.moves.map((slot) => {
    const id = upgrades[slot.id];
    if (!id) return slot;
    learned.push(id);
    const spent = moveById(slot.id).pp - slot.pp;
    return { id, pp: Math.max(0, Math.min(slot.pp, moveById(id).pp - spent)) };
  });
  return learned;
}

export interface Evolution {
  from: SpeciesId;
  to: SpeciesId;
  level: number;
  upgradedMoves?: string[];
}

export interface GrowthResult {
  leveled: boolean;
  levels: number;
  evolutions: Evolution[];
  /** Auto-slotted because the kit still had room. */
  learned: string[];
  /** Needs victory learn/forget/skip — kit already holds four. */
  pendingLearns: string[];
}

/** Append when a slot is free. Never silently overwrites — battle owns forget/skip. */
export function tryLearnMove(m: Mon, id: string): "learned" | "known" | "full" {
  if (m.moves.some((slot) => slot.id === id)) return "known";
  if (m.moves.length >= 4) return "full";
  m.moves.push({ id, pp: moveById(id).pp });
  return "learned";
}

/** Swap one known slot for a newly offered move (player picked the victim). */
export function replaceMove(m: Mon, index: number, id: string): void {
  if (index < 0 || index >= m.moves.length) return;
  m.moves[index] = { id, pp: moveById(id).pp };
}

function learnAtLevel(m: Mon, level: number): { learned: string[]; pending: string[] } {
  const sp = speciesById(m.speciesId);
  const learned: string[] = [];
  const pending: string[] = [];
  // A partner caught in early grass remembers the heavy moves its kind starts with.
  const remembered = level === EARLY_LEVEL ? sp.moves.filter((id) => moveById(id).power > EARLY_POWER) : [];
  const offers = [...remembered, ...(sp.learnset ?? []).filter((entry) => entry.level === level).map((entry) => entry.id)];
  for (const id of offers) {
    const result = tryLearnMove(m, id);
    if (result === "learned") learned.push(id);
    else if (result === "full") pending.push(id);
  }
  return { learned, pending };
}

export function grantExp(m: Mon, amount: number): GrowthResult {
  m.exp += Number.isFinite(amount) ? Math.max(0, Math.floor(amount)) : 0;
  let levels = 0;
  const evolutions: Evolution[] = [];
  const learned: string[] = [];
  const pendingLearns: string[] = [];
  while (m.level < LEVEL_CAP && m.exp >= expToNext(m.level)) {
    m.exp -= expToNext(m.level);
    const ratio = m.hp / maxHp(m);
    m.level += 1;
    const taught = learnAtLevel(m, m.level);
    learned.push(...taught.learned);
    pendingLearns.push(...taught.pending);
    const sp = speciesById(m.speciesId);
    if (sp.evolvesTo && sp.evolveLevel !== undefined && m.level >= sp.evolveLevel) {
      m.speciesId = sp.evolvesTo;
      // Preserve identity and custom moves; inherited slots gain their evolved signatures.
      if (m.nick === sp.name) m.nick = speciesById(sp.evolvesTo).name;
      const upgradedMoves = upgradeMoves(m);
      evolutions.push({ from: sp.id, to: sp.evolvesTo, level: m.level,
        ...(upgradedMoves.length ? { upgradedMoves } : {}) });
    }
    m.hp = ratio === 0 ? 0 : Math.max(1, Math.round(maxHp(m) * ratio));
    levels += 1;
  }
  if (m.level >= LEVEL_CAP) m.exp = 0;
  return { leveled: levels > 0, levels, evolutions, learned, pendingLearns };
}

export function isFaint(m: Mon): boolean {
  return m.hp <= 0;
}
